import MsgTrans from '@components/common/i18n/message-trans'
import { AcademicCap } from '@components/common/icons/custom-icons'
import { FastlyImage } from '@components/common/image'
import { Trans } from '@lingui/macro'
import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import {
  BookOpen,
  Check,
  Clock,
  Download,
  FileText,
  AlertTriangle,
  Play,
} from 'react-feather'

const COMMON_IMAGE_PATH = '/images/home/courses/card/v4/'
const FALLBACK_COURSE_IMAGE = '/images/home/courses/coding-prodigy.png'

const HOVER_BG = [
  'hover:bg-orange-100 hover:border-orange',
  'hover:bg-orange2-50 hover:border-orange2-400 hover:bg-opacity-75',
  'hover:bg-grey-200 hover:border-grey',
  'hover:bg-blue-50 hover:border-blue-300 hover:bg-opacity-50',
  'hover:bg-red-50 hover:border-red-200',
  'hover:bg-yellow-100 hover:bg-opacity-50 hover:border-yellow-200',
  'hover:bg-violet-50 hover:border-violet-100',
  'hover:bg-pink hover:border-pink',
]

type CourseCardProps = {
  course: any // Adjust type based on actual structure
  onPDFDownload: (courseCode: string, gradeOptions: number[]) => void
  isCoursePage?: boolean
  onVideoModalOpen?: (videos: any[], title: React.ReactNode) => void
  utmSource?: string
  slug?: string
}

// Clamp the course outline to 3 lines. When it overflows, "view full
// description" reveals the full text in a floating tooltip that overlays the
// card instead of expanding it inline, so the row of cards never shifts (no
// CLS). Hovering or clicking opens it and it stays put even after the pointer
// leaves (dismissed by clicking the link again or anywhere outside the card).
const CourseOutline = ({ children }: { children: React.ReactNode }) => {
  const textRef = useRef<HTMLParagraphElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [isTruncated, setIsTruncated] = useState(false)
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)
  const [fullText, setFullText] = useState('')

  useEffect(() => {
    const element = textRef.current
    if (!element) return
    setIsTruncated(element.scrollHeight > element.clientHeight + 1)
    setFullText(element.textContent || '')
  }, [children])

  useEffect(() => {
    if (!isTooltipOpen) return
    const handleClickOutside = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setIsTooltipOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isTooltipOpen])

  return (
    <div className="text-sm" style={{ minHeight: '70px' }}>
      <p
        ref={textRef}
        style={{
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {children}
      </p>
      {isTruncated && (
        <div
          ref={wrapperRef}
          className="relative inline-block"
          onMouseEnter={() => setIsTooltipOpen(true)}
        >
          <button
            type="button"
            aria-expanded={isTooltipOpen}
            onClick={() => setIsTooltipOpen((prev) => !prev)}
            className="text-grey underline focus:outline-none"
          >
            <Trans>view full description</Trans>
          </button>
          {isTooltipOpen && (
            <div
              role="tooltip"
              className="absolute z-30 bottom-full left-0 mb-2 w-64 max-w-xs rounded-md bg-grey-900 p-3 text-xs leading-snug text-white shadow-lg"
            >
              {fullText}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const CourseCard = ({
  course,
  onPDFDownload,
  isCoursePage,
  onVideoModalOpen,
  utmSource,
  slug,
}: CourseCardProps) => {
  const getRandomHoverBg = () => {
    const randomIndex = Math.floor(Math.random() * HOVER_BG.length)
    return HOVER_BG[randomIndex]
  }
  const premiumCourses = ['cp', 'cc', 'cgm']
  const courseImageSrc = course.thumbnailLandscape
    ? course.thumbnailLandscape
    : course.image
    ? `${COMMON_IMAGE_PATH}${course.image}.jpg`
    : FALLBACK_COURSE_IMAGE
  const courseTitle = course.titleCompactView || course.title
  const shouldShowDurationSuffix = !course.hideDurationSuffix
  const hasCurriculumLink =
    course.curriculumPDFLink && course.curriculumPDFLink.length > 0
  const hasTrialCTA =
    course.trialCourseQuery && course.trialCourseQuery.length > 0
  const hasCoursePageUrl =
    course.coursePageUrl && course.coursePageUrl.length > 0

  return (
    <div
      className={`p-1 w-full ${
        isCoursePage
          ? 'block sm:hidden md:block lg:hidden md:w-80 max-w-md'
          : 'md:w-1/2 lg:w-1/3 xl:w-1/4 max-w-md'
      }`}
    >
      <div
        className={`pb-0 shadow hover:shadow-lg transition-all duration-200 rounded-md bg-white ${getRandomHoverBg()} border border-grey-100 h-full flex flex-col justify-between`}
      >
        {course.isAICourse && (
          <div className="relative top-0 left-0 z-10 mx-1">
            <div className="text-white font-700 text-lg p-2 absolute z-10">
              AI
            </div>
            <img
              src="/images/home/courses/bookmark.svg"
              alt="bookmark"
              className="absolute"
            />
          </div>
        )}
        <div className="relative w-full p-2">
          <div
            className="rounded-md overflow-hidden relative"
            style={{ aspectRatio: '422 / 181' }}
          >
            <FastlyImage
              src={courseImageSrc}
              alt={course.altText}
              className="w-full h-full object-cover"
              width={422}
              height={181}
            />
            {course.curriculumVideos && (
              <button
                className="absolute w-full h-full z-10 bottom-0 flex group"
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  onVideoModalOpen?.(
                    course?.curriculumVideos,
                    courseTitle as string
                  )
                }}
              >
                <span className="p-3 bg-white bg-opacity-75 m-auto rounded-full transition-all duration-200 group-hover:shadow-lg group-hover:bg-orange">
                  <Play size={16} fill="#000" />
                </span>
              </button>
            )}
          </div>
          <div className="absolute top-0 right-0 z-10 px-2 font-500 text-base m-2 bg-white bg-opacity-50 rounded-bl-md">
            <Trans>Age</Trans> {course.Age}
          </div>
        </div>

        <div className="space-y-2 p-2 flex flex-col flex-1">
          <h3
            className={`leading-tight font-600 duration-300 cursor-pointer ${
              course.coursePageUrl ? 'hover:text-orange' : ''
            }`}
            style={{ fontSize: '1.35rem' }}
          >
            {hasCoursePageUrl ? (
              <a
                href={course.coursePageUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {course.title}
              </a>
            ) : (
              <>{course.title}</>
            )}
          </h3>
          <div className="grid grid-cols-2 text-sm gap-x-1">
            <div className="flex items-center">
              <AcademicCap className="text-orange w-5" />
              <span className="mt-1" style={{ marginLeft: '2px' }}>
                <Trans>Grade</Trans> {course.grade}
              </span>
            </div>
            <div className="flex items-center">
              <FileText className="text-orange w-4 mr-1" />
              <span>
                {course.activites}+ <Trans>Activities</Trans>
              </span>
            </div>
            <div className="flex items-center">
              <BookOpen
                className="text-orange w-4 mr-1"
                style={{ marginLeft: '2px' }}
              />
              <span>
                {course.lessons} <Trans>Lessons</Trans>
              </span>
            </div>
            <div className="flex items-center">
              <Clock className="text-orange w-4 mr-1" />
              <span>
                {course.duration}{' '}
                {shouldShowDurationSuffix ? <Trans>months</Trans> : null}
              </span>
            </div>
          </div>
          {!isCoursePage && (
            <CourseOutline>
              <MsgTrans msg={course.description} />
            </CourseOutline>
          )}

          <h4 className="font-600">
            <Trans>Learning outcomes</Trans>
          </h4>

          <div className="space-y-1 text-sm">
            {course.outcomes.slice(0, 4).map((outcome, index) => (
              <div key={index} className="flex items-start">
                <span
                  className="rounded-full bg-orange-200"
                  style={{ padding: '2px', marginTop: '2px' }}
                >
                  <Check
                    height={9}
                    width={9}
                    strokeWidth={3}
                    className="text-orange"
                  />
                </span>
                <p className="ml-1 text-left">
                  <MsgTrans msg={outcome} />
                </p>
              </div>
            ))}
          </div>

          {course.prerequisite && (
            <div className="bg-blue-50 bg-opacity-50 text-blue-300 p-1 rounded-md flex">
              <AlertTriangle className="w-4 h-4 mr-1 inline-block" />
              <span className="text-sm line-height-1">
                <Trans>
                  <span className="font-600">Prerequisite:</span>{' '}
                  {course.prerequisite}
                </Trans>
              </span>
            </div>
          )}
        </div>

        <div className="relative w-full">
          {premiumCourses.includes(course.courseCode) ? (
            <button
              className="text-sm flex items-center align-middle font-600 py-2 h-10 text-orange mx-auto focus:outline-none z-20 hover:underline relative"
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                onPDFDownload(course.courseCode, course.gradeOptions)
              }}
            >
              <span className="px-1">
                <Trans>Download curriculum</Trans>
              </span>
              <Download size={15} />
            </button>
          ) : hasCurriculumLink ? (
            <a
              href={course.curriculumPDFLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm flex items-center justify-center align-middle font-600 h-10 text-orange mx-auto focus:outline-none z-10 hover:underline"
            >
              <span className="px-1">
                <Trans>Download curriculum</Trans>
              </span>
              <Download size={15} />
            </a>
          ) : null}
          {hasTrialCTA ? (
            <div className="px-3 mb-4">
              <Link
                href={`/register/?course=${
                  course.trialCourseQuery
                }&utm_medium=homepage-course-card${
                  utmSource ? `&utm_source=${utmSource}` : ''
                }${slug ? `&slug=${slug}` : ''}`}
                prefetch={false}
              >
                <button className="btn border-orange text-orange hover:bg-orange hover:text-white duration-300 w-full text-center text-lg">
                  <Trans>Try a free lesson</Trans>
                </button>
              </Link>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default CourseCard
